import type { OrgNode } from '@/types/analytics.types';
import { formatScore } from '@/utils/formatScore';

/** Fallback color palette for fundamentals when the API doesn't return a color */
export const FUND_DEFAULT_COLORS = [
  '#342F82', '#e05c22', '#005ba1', '#00a651',
  '#f3c300', '#9b27af', '#0ea5e9', '#f43f5e',
];

/** Color according to score 1-4 */
export function scoreColor(v: number): string {
  if (v >= 3.5) return '#059669';
  if (v >= 3.0) return '#10b981';
  if (v >= 2.5) return '#f59e0b';
  return '#ef4444';
}

/** Score → badge style { bg, text, ring } */
export function scoreStyle(v: number): { bg: string; text: string; ring: string } {
  if (v >= 3.5) return { bg: '#d1fae5', text: '#064e3b', ring: '#34d399' };
  if (v >= 3.0) return { bg: '#ecfdf5', text: '#065f46', ring: '#6ee7b7' };
  if (v >= 2.5) return { bg: '#fefce8', text: '#713f12', ring: '#fde047' };
  if (v >= 2.0) return { bg: '#fffbeb', text: '#92400e', ring: '#fcd34d' };
  return { bg: '#fef2f2', text: '#991b1b', ring: '#fca5a5' };
}

// `getInitials` vive en `@/utils/strings` desde 2026-09-03: había dos implementaciones con reglas
// distintas y la misma persona salía con iniciales diferentes según la pantalla. Se reexporta para
// no tocar a los consumidores que ya la importaban de aquí.
export { getInitials } from '@/utils/strings';

/** Find a node by id in an OrgNode tree */
export function findNode(nodes: OrgNode[], id: string): OrgNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

/** Collect all Person-type leaf nodes from an OrgNode tree */
export function getAllPeople(nodes: OrgNode[]): OrgNode[] {
  const result: OrgNode[] = [];
  function walk(ns: OrgNode[]) {
    for (const n of ns) {
      if (n.t === 'Person') result.push(n);
      if (n.children) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

/** Clamp a number to a range [min, max] */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Formatea un puntaje para la analítica. Delega en `formatScore`, el único formateador de
 * decimales de la aplicación (un decimal), para que la analítica y el resto de las pantallas
 * nunca muestren precisiones distintas del mismo dato.
 */
export function fmtScore(value: number | null | undefined): string {
  return formatScore(value);
}
